import { useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { isQZWebSocketActive, printReceipt, getPrinterSettings } from "@/lib/qz-tray";
import {
  CentralizedPrintJob,
  fetchPendingPrintJobs,
  claimJobForProcessing,
  markJobAsPrinted,
  incrementJobAttempt,
  cleanupOldPrintJobs,
  MAX_RETRY_ATTEMPTS,
  RETRY_DELAY_MS,
} from "@/lib/centralized-print-queue";
import { toast } from "sonner";
import { safeRemoveChannel } from "@/lib/supabase-channel";
import { getPrintMode } from "@/lib/print-dispatcher";

const PROCESS_INTERVAL = 5000;
const CLEANUP_INTERVAL = 3600000;
const REALTIME_DEBOUNCE_MS = 500;

/**
 * Hook that listens for pending print jobs in the database and processes them.
 * Should only be active on devices that have QZ Tray connected (usually the PC).
 */
export function useCentralizedPrintQueue(enabled: boolean) {
  const { activeRestaurant } = useRestaurantContext();
  const processIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cleanupIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isProcessingRef = useRef(false);
  const realtimeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const localModeLoggedRef = useRef(false);

  /**
   * Decodes Base64 content back to original ESC/POS string.
   * Falls back to original content if not Base64 encoded (old jobs).
   */
  const decodeContentFromDatabase = useCallback((content: string): string => {
    try {
      return decodeURIComponent(escape(atob(content)));
    } catch {
      // Fallback: content might not be Base64 encoded (old jobs)
      console.log("[PRINT-QUEUE] Content not Base64, using as-is");
      return content;
    }
  }, []);

  const processJob = useCallback(async (job: CentralizedPrintJob) => {
    try {
      // Optimistic lock: claim the job atomically before printing
      const claimed = await claimJobForProcessing(job.id);
      if (!claimed) {
        return false; // Another instance already processing this job
      }

      console.log(`[PRINT-QUEUE] Processing job ${job.id} for printer "${job.printer_name}" (attempt ${(job.attempts || 0) + 1}/${MAX_RETRY_ATTEMPTS})`);
      
      // Check if QZ is still active before processing
      if (!isQZWebSocketActive()) {
        const errorMsg = `QZ Tray desconectado`;
        console.error(`[PRINT-QUEUE] ${errorMsg}`);
        await incrementJobAttempt(job.id, errorMsg);
        return false;
      }
      
      // Decode Base64 content back to original ESC/POS string
      const decodedContent = decodeContentFromDatabase(job.content);
      console.log(`[PRINT-QUEUE] Decoded content length: ${decodedContent.length} chars`);
      
      // Fetch printer settings for correct protocol handling
      const printerSettings = await getPrinterSettings(job.printer_name, job.restaurant_id);
      console.log(`[PRINT-QUEUE] Printer settings:`, printerSettings ? `protocol=${printerSettings.protocol}` : "not found");
      
      // CRITICAL: Check the return value of printReceipt
      const printSuccess = await printReceipt(job.printer_name, decodedContent, {
        logType: "pedidos",
        logIdentifier: job.order_number,
        locationName: job.location_name || undefined,
        settings: printerSettings || undefined,
      });
      
      if (!printSuccess) {
        const errorMsg = `Impressora "${job.printer_name}" não respondeu`;
        console.error(`[PRINT-QUEUE] ${errorMsg}`);
        await incrementJobAttempt(job.id, errorMsg);
        return false;
      }
      
      const markedOk = await markJobAsPrinted(job.id);
      if (!markedOk) {
        console.error(`[PRINT-QUEUE] Job ${job.id} printed but failed to confirm in DB — will NOT retry print`);
        // Don't return false here to avoid re-printing; the trigger will auto-update updated_at
        // on next poll it will try to mark again
        return true; // Print succeeded physically
      }
      console.log(`[PRINT-QUEUE] Job ${job.id} printed and confirmed in DB`);
      return true;
    } catch (error) {
      const baseError = error instanceof Error ? error.message : "Erro desconhecido";
      console.error(`[PRINT-QUEUE] ${baseError}`);
      await incrementJobAttempt(job.id, baseError);
      return false;
    }
  }, [decodeContentFromDatabase]);

  const processPendingJobs = useCallback(async () => {
    // Skip if offline to avoid unnecessary errors
    if (!navigator.onLine) {
      console.log("[PRINT-QUEUE] Offline, skipping job fetch");
      return;
    }
    if (!activeRestaurant?.id || isProcessingRef.current) return;
    // Fase 4 (belt-and-suspenders): em local_service o servidor local imprime — o consumidor QZ
    // NÃO processa a fila (mesmo que algum job perdido apareça). Loga uma vez p/ diagnóstico.
    if ((await getPrintMode(activeRestaurant.id)) === "local_service") {
      if (!localModeLoggedRef.current) {
        console.log("[PRINT-QUEUE] print_mode=local_service — fila da nuvem ignorada (servidor local imprime)");
        localModeLoggedRef.current = true;
      }
      return;
    }
    if (!isQZWebSocketActive()) return;

    isProcessingRef.current = true;

    try {
      const jobs = await fetchPendingPrintJobs(activeRestaurant.id);

      if (jobs.length === 0) return;

      // Filtrar jobs em cooldown (aguardando retry)
      const now = Date.now();
      const readyJobs = jobs.filter(job => {
        const attempts = job.attempts || 0;
        if (attempts === 0) return true;
        
        // Backoff progressivo: 30s * número de tentativas, using updated_at for accuracy
        const cooldownMs = RETRY_DELAY_MS * attempts;
        const lastUpdateTime = new Date(job.updated_at || job.created_at).getTime();
        return (now - lastUpdateTime) > cooldownMs;
      });

      if (readyJobs.length === 0) return;

      console.log(`[PRINT-QUEUE] Processing ${readyJobs.length} ready job(s) (${jobs.length - readyJobs.length} in cooldown)`);

      let successCount = 0;

      for (const job of readyJobs) {
        const success = await processJob(job);
        if (success) {
          successCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} impressão(ões) processada(s)`);
      }
    } catch (error) {
      console.error("[PRINT-QUEUE] Error processing jobs:", error);
    } finally {
      isProcessingRef.current = false;
    }
  }, [activeRestaurant?.id, processJob]);

  // Log network status changes
  useEffect(() => {
    const handleOffline = () => console.log("[PRINT-QUEUE] Network went offline");
    const handleOnline = () => {
      console.log("[PRINT-QUEUE] Network back online");
      processPendingJobs(); // Process any pending jobs when back online
    };
    
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [processPendingJobs]);

  // Process pending jobs periodically and on new inserts
  useEffect(() => {
    if (!enabled || !activeRestaurant?.id) return;

    // Process immediately on mount
    processPendingJobs();

    // Setup periodic processing
    processIntervalRef.current = setInterval(processPendingJobs, PROCESS_INTERVAL);

    // Setup realtime subscription for immediate processing
    const channel = supabase
      .channel("pending-print-jobs")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "pending_print_jobs",
          filter: `restaurant_id=eq.${activeRestaurant.id}`,
        },
        () => {
          console.log("[PRINT-QUEUE] New job detected via realtime, debouncing...");
          if (realtimeDebounceRef.current) {
            clearTimeout(realtimeDebounceRef.current);
          }
          realtimeDebounceRef.current = setTimeout(() => {
            processPendingJobs();
          }, REALTIME_DEBOUNCE_MS);
        }
      )
      .subscribe((status) => {
        console.log(`[PRINT-QUEUE] Realtime subscription status: ${status}`);
      });

    // Setup cleanup interval
    cleanupIntervalRef.current = setInterval(() => {
      if (activeRestaurant?.id) {
        cleanupOldPrintJobs(activeRestaurant.id);
      }
    }, CLEANUP_INTERVAL);

    return () => {
      if (processIntervalRef.current) {
        clearInterval(processIntervalRef.current);
      }
      if (cleanupIntervalRef.current) {
        clearInterval(cleanupIntervalRef.current);
      }
      if (realtimeDebounceRef.current) {
        clearTimeout(realtimeDebounceRef.current);
      }
      safeRemoveChannel(channel);
    };
  }, [enabled, activeRestaurant?.id, processPendingJobs]);

  return { processPendingJobs };
}
